<template>
  <div class="max-w-7xl mx-auto">
    <!-- Header Section -->
    <div class="bg-background-light rounded-lg shadow-sm mb-6">
      <div class="px-6 py-4 border-b border-background-lighter">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 class="text-xl font-semibold text-text-primary">My Orders</h2>
            <p class="text-sm text-text-secondary mt-1">Track and manage your orders</p>
          </div>

          <!-- Filters -->
          <div class="mt-3 sm:mt-0 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <select
                v-model="filters.status"
                @change="onFilterChange"
                class="px-3 py-2 border border-background-lighter rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-text-primary"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <input
                v-model="filters.search"
                @input="debounceSearch"
                type="text"
                placeholder="Search orders..."
                class="px-3 py-2 border border-background-lighter rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary w-full sm:w-64 text-text-primary placeholder-text-secondary"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Content Section -->
    <div class="bg-background-light rounded-lg shadow-sm">
      <div class="p-0">
        <!-- Loading State -->
        <div v-if="isLoading" class="flex justify-center items-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span class="ml-3 text-text-secondary">Loading orders...</span>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center py-12">
          <div class="text-red-600 mb-3">{{ error }}</div>
          <button
              @click="fetchOrders"
              class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
          >
            Retry
          </button>
        </div>

        <!-- Orders List -->
        <div v-else-if="orders.length > 0" class="divide-y divide-background-lighter">
          <template v-for="(group, groupIndex) in groupedOrders" :key="groupIndex">
            <!-- Cart Group Header -->
            <div v-if="group.isCart" class="cart-header">
              <div class="px-6 py-3">
                <div class="flex justify-content-between items-center">
                  <div class="flex items-center">
                    <ShoppingCartIcon class="w-5 h-5 cart-header-icon mr-2" />
                    <span class="font-medium text-text-primary">Order Group #ESK-{{ group.cartId }}</span>
                  </div>
                  <div class="text-sm text-text-secondary">
                    {{ group.orders.length }} orders | {{ formatDate(group.orders[0].created_at) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Orders in this group -->
            <div
                v-for="order in group.orders"
                :key="order.id"
                :class="[
                'border-background-lighter hover:bg-background-lighter transition-colors',
                group.isCart ? 'cart-group' : ''
              ]"
            >
              <div class="px-6 py-4">
                <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                  <!-- Order Info -->
                  <div class="flex-1">
                    <div class="flex items-center space-x-3 mb-2">
                      <h3 class="font-semibold text-blue-600">#ESK-{{ order.id }}</h3>
                      <span :class="getStatusClass(order.status)" class="px-3 py-1 rounded-full text-xs font-medium">
                        {{ getStatusText(order.status) }}
                      </span>
                      <span v-if="order.payment_status" class="px-2 py-1 bg-background-lighter text-text-secondary rounded text-xs">
                        {{ order.payment_status === 'pending' ? 'Payment Pending' : 'Paid' }}
                      </span>
                    </div>

                    <div class="text-sm text-text-secondary space-y-1">
                      <p><span class="font-medium">Product:</span> {{ order.article_title || order.article_libelle || 'N/A' }}</p>
                      <p><span class="font-medium">Quantity:</span> {{ order.quantity || 1 }}</p>
                      <p><span class="font-medium">Date:</span> {{ formatDate(order.created_at) }}</p>
                      <p v-if="order.player_info || order.playerinfo">
                        <span class="font-medium">Player Info:</span> {{ order.player_info || order.playerinfo }}
                      </p>
                    </div>
                  </div>

                  <!-- Order Amount & Actions -->
                  <div class="flex flex-col lg:flex-row lg:items-center lg:space-x-6">
                    <!-- Price -->
                    <div class="text-right mb-3 lg:mb-0">
                      <div v-if="!authStore.shouldHidePrices" class="text-lg font-bold text-text-primary">
                        <span v-if="order.coupon_discount > 0" class="line-through text-text-secondary text-sm mr-2">
                          {{ formatCurrency(order.coupon_discount + order.amount, order.is_da ) }}
                        </span>
                        {{ formatCurrency(order.amount, order.is_da) }}
                      </div>
                      <div v-else class="text-lg font-bold text-text-secondary">****</div>
                    </div>

                    <!-- Actions -->
                    <div class="flex flex-wrap gap-2">
                      <button
                          @click="viewOrderDetails(order)"
                          class="px-3 py-2 text-xs bg-primary text-white rounded hover:bg-primary-dark transition-colors"
                      >
                        View Details
                      </button>
                      <button
                          @click="openChat(order)"
                          :disabled="order.chat_disabled && (!order.messages_count || order.messages_count === 0)"
                          class="px-3 py-2 text-xs inline-flex items-center rounded transition-colors"
                          :class="order.chat_disabled && (!order.messages_count || order.messages_count === 0) ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'"
                      >
                        <svg class="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                        </svg>
                        Messages
                        <span v-if="order.messages_count > 0" class="ml-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-white rounded-full min-w-[18px] min-h-[18px]" :class="order.unread_messages_count > 0 ? 'bg-red-500' : 'bg-gray-400'">
                          {{ order.messages_count }}
                        </span>
                      </button>
                      <button
                          v-if="order.status === 'pending' || order.status.toString()  === '0'"
                          @click="cancelOrder(order)"
                          :disabled="isCancelling === order.id"
                          class="px-3 py-2 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center"
                      >
                        <div v-if="isCancelling === order.id" class="animate-spin -ml-1 mr-1 h-3 w-3 text-white">
                          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                        {{ isCancelling === order.id ? 'Cancelling...' : 'Cancel' }}
                      </button>

                      <button
                          v-if="canUploadProof(order)"
                          @click="uploadPaymentProof(order)"
                          class="px-3 py-2 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
                      >
                        Upload Proof
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Cart Summary -->
            <div v-if="group.isCart && group.summary" class="cart-summary">
              <div class="px-6 py-2 text-sm text-text-secondary text-right">
                <strong>Group Summary:</strong>
                Total: {{ formatCurrency(group.summary.total, group.summary.is_da) }} |
                Items: {{ group.summary.items }}
              </div>
            </div>
          </template>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-12">
          <ShoppingBagIcon class="mx-auto h-12 w-12 text-text-secondary" />
          <h3 class="mt-2 text-sm font-medium text-text-primary">No orders found</h3>
          <p class="mt-1 text-sm text-text-secondary">Your orders will appear here once you make a purchase</p>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.total > pagination.per_page" class="px-6 py-4 bg-background-lighter border-t border-background-lighter">
          <div class="flex items-center justify-between">
            <!-- Results info -->
            <div class="text-sm text-text-secondary">
              Showing {{ ((pagination.current_page - 1) * pagination.per_page) + 1 }} to
              {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }}
              of {{ pagination.total }} results
            </div>

            <!-- Pagination controls -->
            <nav class="flex items-center space-x-2">
              <button
                  @click="changePage(pagination.current_page - 1)"
                  :disabled="pagination.current_page === 1"
                  class="px-3 py-2 text-sm bg-background border border-background-lighter rounded hover:bg-background-lighter disabled:opacity-50 disabled:cursor-not-allowed text-text-primary"
              >
                Previous
              </button>

              <!-- Page numbers -->
              <template v-for="page in getPageNumbers()" :key="page">
                <button
                    v-if="page !== '...'"
                    @click="changePage(page)"
                    :class="[
                    'px-3 py-2 text-sm border rounded',
                    pagination.current_page === page
                      ? 'bg-primary text-white border-primary'
                      : 'bg-background border-background-lighter hover:bg-background-lighter text-text-primary'
                  ]"
                >
                  {{ page }}
                </button>
                <span v-else class="px-3 py-2 text-sm text-text-secondary">...</span>
              </template>

              <button
                  @click="changePage(pagination.current_page + 1)"
                  :disabled="pagination.current_page === pagination.last_page"
                  class="px-3 py-2 text-sm bg-background border border-background-lighter rounded hover:bg-background-lighter disabled:opacity-50 disabled:cursor-not-allowed text-text-primary"
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Order Details Modal -->
    <OrderDetailsModal
        v-if="selectedOrder"
        :isOpen="isModalOpen"
        :order="selectedOrder"
        @close="closeModal"
    />

    <!-- Chat Modal -->
    <ChatModal
        v-if="chatOrder"
        :isOpen="isChatOpen"
        :order="chatOrder"
        @close="closeChat"
    />
  </div>
</template>





<script>
import {computed, onMounted, ref, watch} from 'vue';
import {useRoute} from 'vue-router';
import {useAuthStore} from '../../stores/auth';
import {useDataStore} from '../../stores/dataStore';
import api from '../../services/api';
import {ShoppingBagIcon, ShoppingCartIcon} from '@heroicons/vue/24/outline';
import OrderDetailsModal from '../../components/OrderDetailsModal.vue';
import ChatModal from '../../components/ChatModal.vue';
import Swal from 'sweetalert2';

export default {
  name: 'Orders',
  components: {
    OrderDetailsModal,
    ChatModal,
    ShoppingBagIcon,
    ShoppingCartIcon
  },
  setup() {
    const route = useRoute();
    const authStore = useAuthStore();
    const dataStore = useDataStore();

    const orders = ref([]);
    const isLoading = ref(true);
    const error = ref(null);
    const selectedOrder = ref(null);
    const isModalOpen = ref(false);
    const chatOrder = ref(null);
    const isChatOpen = ref(false);
    const isCancelling = ref(null);

    const filters = ref({
      status: '',
      search: ''
    });

    const pagination = ref({
      current_page: 1,
      last_page: 1,
      per_page: 10,
      total: 0
    });

    let searchTimeout = null;

    // Group orders by cart_id
    // Group orders by cart_id while maintaining sort order
    const groupedOrders = computed(() => {
      const groups = [];
      const cartGroups = {};
      const processedOrderIds = new Set();

      // First pass: create cart groups
      orders.value.forEach(order => {
        if (order.cart_id && !processedOrderIds.has(order.id)) {
          if (!cartGroups[order.cart_id]) {
            cartGroups[order.cart_id] = {
              orders: [],
              firstOrderDate: order.created_at,
              firstOrderId: order.id
            };
          }
          cartGroups[order.cart_id].orders.push(order);
          processedOrderIds.add(order.id);
        }
      });

      // Process orders in original sorted order
      orders.value.forEach(order => {
        if (processedOrderIds.has(order.id) && order.cart_id) {
          // This is a cart order - check if it's the first one we encounter
          const cartGroup = cartGroups[order.cart_id];
          if (cartGroup && cartGroup.firstOrderId === order.id) {
            // Only add the cart group once when we encounter its first order
            if (cartGroup.orders.length > 1) {
              // Calculate cart summary
              const summary = {
                total: cartGroup.orders.reduce((sum, o) => sum + (o.amount || 0), 0),
                items: cartGroup.orders.reduce((sum, o) => sum + (o.quantity || 1), 0),
                is_da: cartGroup.orders[0]?.is_da || 0
              };

              groups.push({
                isCart: true,
                cartId: order.cart_id,
                orders: cartGroup.orders.sort((a, b) => b.id - a.id), // Sort orders within group
                summary: summary,
                sortKey: cartGroup.firstOrderId // Use first order id for sorting
              });
            } else {
              // Single order in cart group, treat as individual
              groups.push({
                isCart: false,
                orders: [order],
                sortKey: order.id
              });
            }
            // Mark this cart as processed
            delete cartGroups[order.cart_id];
          }
        } else if (!order.cart_id) {
          // Regular single order
          groups.push({
            isCart: false,
            orders: [order],
            sortKey: order.id
          });
        }
      });

      // Ensure groups maintain sort order by their first order's ID
      return groups.sort((a, b) => b.sortKey - a.sortKey);
    });

    const formatCurrency = (amount, is_da = null) => {
      if (!amount && amount !== 0) return '0.00';

      // Get currency symbol based on is_da parameter
      let symbol = '$'; // Default

      if (is_da !== null && is_da !== undefined) {
        switch (parseInt(is_da)) {
          case 2:
            symbol = 'Dzd';
            break;
          case 1:
            symbol = '€';
            break;
          case 3:
            symbol = 'EGP';
            break;
          default:
            symbol = '$';
            break;
        }
      } else {
        // Fallback to dataStore if is_da not provided
        symbol = dataStore?.currencySymbol || '$';
      }


      const convertedAmount = (typeof amount === 'number' ? amount.toFixed(2) : parseFloat(amount || 0).toFixed(2));


      // Format display based on currency type
      switch (parseInt(is_da)) {
        case 2: // Dzd - Display after amount
          return `${convertedAmount} ${symbol}`;
        case 1: // Euro - Display before amount
          return `${symbol}${convertedAmount}`;
        case 3: // EGP - Display after amount
          return `${convertedAmount} ${symbol}`;
        default: // Dollar - Display before amount
          return `${symbol}${convertedAmount}`;
      }
    };


    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return 'Invalid Date';
      }
    };

    const getStatusClass = (status) => {
      const statusNum = typeof status === 'string' ? parseInt(status) : status;
      const classes = {
        0: 'bg-yellow-100 text-yellow-800', // pending
        1: 'bg-green-100 text-green-800',   // completed
        4: 'bg-blue-100 text-blue-800',     // processing
        '-3': 'bg-orange-100 text-orange-800', // processing cancellation
        '-1': 'bg-red-100 text-red-800'     // cancelled
      };
      return classes[statusNum] || 'bg-gray-100 text-gray-800';
    };

    const getStatusText = (status) => {
      const statusNum = typeof status === 'string' ? parseInt(status) : status;
      const texts = {
        0: 'Pending',
        1: 'Completed',
        4: 'Processing',
        '-3': 'Cancelling',
        '-1': 'Cancelled'
      };
      return texts[statusNum] || 'Unknown';
    };


    const canUploadProof = (order) => {
      const statusNum = typeof order.status === 'string' ? parseInt(order.status) : order.status;

      if (statusNum !== 0 && statusNum !== 'pending') {
        return false;
      }

      // OCPay and PayPal are handled via API redirect, no manual proof needed
      if (order.payment_method === 'ocpay' || order.payment_method === 'paypal') {
        return false;
      }

      return order.payment_id &&
          order.payment_method !== 'wallet' &&
          ['pending', 'rejected'].includes(order.payment_status);
    };




    const fetchOrders = async (page = 1) => {
      isLoading.value = true;
      error.value = null;

      const pageNum = Math.max(1, parseInt(page) || 1);

      try {
        const params = {
          page: pageNum,
          status: filters.value.status,
          search: filters.value.search,
          sort: 'desc', // Add this - sort by newest first
          order_by: 'id' // Add this - order by id (or 'created_at')
        };

        // Remove empty parameters
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key];
          }
        });

        const response = await api.get('/user/orders', { params });

        if (response.data && response.data.success) {
          // Sort orders client-side as backup (in case API doesn't sort)
          orders.value = (response.data.data || []);

          pagination.value = {
            current_page: response.data.current_page || 1,
            last_page: response.data.last_page || 1,
            per_page: response.data.per_page || 10,
            total: response.data.total || 0
          };
        } else {
          orders.value = [];
        }

      } catch (err) {
        console.error('Failed to fetch orders:', err);
        error.value = err.response?.data?.message || 'Failed to load orders. Please try again.';
        orders.value = [];
      } finally {
        isLoading.value = false;
      }
    };


    // New method to handle filter changes - always reset to page 1
    const onFilterChange = () => {
      fetchOrders(1);
    };

    const debounceSearch = () => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        fetchOrders(1); // Always reset to page 1 for search
      }, 300);
    };

    const changePage = (page) => {
      const pageNum = Math.max(1, parseInt(page) || 1);
      if (pageNum >= 1 && pageNum <= pagination.value.last_page) {
        fetchOrders(pageNum);
      }
    };

    const getPageNumbers = () => {
      const pages = [];
      const current = pagination.value.current_page;
      const total = pagination.value.last_page;

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        }
      }

      return pages;
    };

    const viewOrderDetails = (order) => {
      selectedOrder.value = order;
      isModalOpen.value = true;
    };

    const closeModal = () => {
      isModalOpen.value = false;
      setTimeout(() => {
        selectedOrder.value = null;
      }, 300);
    };

    const openChat = (order) => {
      chatOrder.value = order;
      isChatOpen.value = true;
    };

    const closeChat = () => {
      if (chatOrder.value) {
        const idx = orders.value.findIndex(o => o.id === chatOrder.value.id);
        if (idx !== -1) orders.value[idx].unread_messages_count = 0;
      }
      isChatOpen.value = false;
      setTimeout(() => { chatOrder.value = null; }, 300);
    };

    const cancelOrder = async (order) => {
      try {
        const result = await Swal.fire({
          title: 'Cancel Order',
          text: `Are you sure you want to cancel order #ESK-${order.id}?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#ef4444',
          cancelButtonColor: '#6b7280',
          confirmButtonText: 'Yes, cancel it!'
        });

        if (result.isConfirmed) {
          isCancelling.value = order.id;

          await api.post(`/user/orders/${order.id}/cancel`);

          // Update order status in the list
          const orderIndex = orders.value.findIndex(o => o.id === order.id);
          if (orderIndex !== -1) {
            orders.value[orderIndex].status = -1;
          }

          Swal.fire({
            title: 'Cancelled!',
            text: 'Your order has been cancelled.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
        }
      } catch (error) {
        console.error('Failed to cancel order:', error);
        Swal.fire({
          title: 'Error!',
          text: error.response?.data?.message || 'Failed to cancel order.',
          icon: 'error'
        });
      } finally {
        isCancelling.value = null;
      }
    };

    const uploadPaymentProof = async (order) => {
      const { value: file } = await Swal.fire({
        title: 'Upload Payment Proof',
        text: 'Accepted: jpg, jpeg, png, webp, pdf (max 5MB)',
        input: 'file',
        inputAttributes: {
          accept: 'image/*,application/pdf',
          'aria-label': 'Upload payment proof'
        },
        showCancelButton: true,
        confirmButtonText: 'Upload',
        cancelButtonText: 'Cancel'
      });

      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        return Swal.fire('File too large', 'Max size is 5MB.', 'error');
      }

      const formData = new FormData();
      formData.append('payment_proof', file);
      formData.append('id', order.payment_id);

      Swal.showLoading();

      try {
        const response = await api.post('/user/upload-proof', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        await Swal.fire('Uploaded!', response.data.message || 'Your payment proof was uploaded.', 'success');
        fetchOrders(pagination.value.current_page); // Refresh current page
      } catch (err) {
        const msg = err?.response?.data?.message || 'Failed to upload payment proof.';
        Swal.fire('Error', msg, 'error');
      } finally {
        Swal.hideLoading();
      }
    };

    const searchByQueryId = () => {
      const queryId = route.query.id;
      if (queryId) {
        const str = String(queryId).trim();
        const digits = str.replace(/[^0-9]/g, '');
        filters.value.search = digits || str;
        fetchOrders(1);
      }
    };

    onMounted(() => {
      const queryId = route.query.id;
      if (queryId) {
        searchByQueryId();
      } else {
        fetchOrders();
      }
    });

    watch(() => route.query.id, (newId) => {
      if (newId) {
        searchByQueryId();
      }
    });

    return {
      orders,
      groupedOrders,
      isLoading,
      error,
      selectedOrder,
      isModalOpen,
      chatOrder,
      isChatOpen,
      isCancelling,
      filters,
      pagination,
      authStore,
      dataStore,
      formatCurrency,
      formatDate,
      getStatusClass,
      getStatusText,
      canUploadProof,
      fetchOrders,
      onFilterChange, // Added this
      debounceSearch,
      changePage,
      getPageNumbers,
      viewOrderDetails,
      closeModal,
      openChat,
      closeChat,
      cancelOrder,
      uploadPaymentProof
    };
  }
}
</script>


<style scoped>
/* Import Inter font for consistency */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

/* Base styling */
* {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* Cart group styling - Light mode */
.cart-group {
  background-color: rgba(59, 130, 246, 0.025);
  border-left: 4px solid rgba(59, 130, 246, 0.3);
}

.cart-header {
  background-color: rgba(59, 130, 246, 0.08);
  border-left: 4px solid #3b82f6;
}

.cart-header-icon {
  color: #3b82f6;
}

.cart-summary {
  background-color: rgba(59, 130, 246, 0.04);
  border-left: 4px solid rgba(59, 130, 246, 0.5);
}

/* Dark mode support */
.dark .cart-group {
  background-color: rgba(59, 130, 246, 0.08);
  border-left: 4px solid rgba(59, 130, 246, 0.4);
}

.dark .cart-header {
  background-color: rgba(59, 130, 246, 0.15);
  border-left: 4px solid #60a5fa;
}

.dark .cart-header-icon {
  color: #60a5fa;
}

.dark .cart-summary {
  background-color: rgba(59, 130, 246, 0.1);
  border-left: 4px solid rgba(59, 130, 246, 0.6);
}

/* Status badges - Enhanced for dark mode */
.bg-yellow-100 {
  background-color: #fef3c7;
}

.text-yellow-800 {
  color: #92400e;
}

.bg-green-100 {
  background-color: #d1fae5;
}

.text-green-800 {
  color: #065f46;
}

.bg-blue-100 {
  background-color: #dbeafe;
}

.text-blue-800 {
  color: #1e40af;
}

.bg-orange-100 {
  background-color: #fed7aa;
}

.text-orange-800 {
  color: #9a3412;
}

.bg-red-100 {
  background-color: #fee2e2;
}

.text-red-800 {
  color: #991b1b;
}

.bg-gray-100 {
  background-color: #f3f4f6;
}

.text-gray-800 {
  color: #1f2937;
}

/* Dark mode status badges */
.dark .bg-yellow-100 {
  background-color: rgba(252, 211, 77, 0.2);
}

.dark .text-yellow-800 {
  color: #fcd34d;
}

.dark .bg-green-100 {
  background-color: rgba(52, 211, 153, 0.2);
}

.dark .text-green-800 {
  color: #34d399;
}

.dark .bg-blue-100 {
  background-color: rgba(96, 165, 250, 0.2);
}

.dark .text-blue-800 {
  color: #60a5fa;
}

.dark .bg-orange-100 {
  background-color: rgba(251, 146, 60, 0.2);
}

.dark .text-orange-800 {
  color: #fb923c;
}

.dark .bg-red-100 {
  background-color: rgba(248, 113, 113, 0.2);
}

.dark .text-red-800 {
  color: #f87171;
}

.dark .bg-gray-100 {
  background-color: rgba(156, 163, 175, 0.2);
}

.dark .text-gray-800 {
  color: #9ca3af;
}

/* Form elements dark mode support */
.dark select,
.dark input[type="text"] {
  background-color: var(--color-background-light);
  border-color: var(--color-background-lighter);
  color: var(--color-text);
}

.dark select:focus,
.dark input[type="text"]:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(0, 230, 118, 0.2);
}

.dark select option {
  background-color: var(--color-background-light);
  color: var(--color-text);
}

/* Button enhancements for dark mode */
.dark button {
  transition: all 0.3s ease;
}

.dark .bg-primary {
  background-color: var(--color-primary);
}

.dark .hover\:bg-primary-dark:hover {
  background-color: var(--color-primary-dark);
}

/* Pagination dark mode */
.dark nav button {
  background-color: var(--color-background-light);
  border-color: var(--color-background-lighter);
  color: var(--color-text);
}

.dark nav button:hover:not(:disabled) {
  background-color: var(--color-background-lighter);
}

.dark nav button:disabled {
  opacity: 0.5;
  background-color: var(--color-background-light);
}

.dark nav button.bg-primary {
  background-color: var(--color-primary);
  border-color: var(--color-primary);
}

/* Enhanced hover effects */
.cart-group:hover {
  background-color: rgba(59, 130, 246, 0.04);
}

.dark .cart-group:hover {
  background-color: rgba(59, 130, 246, 0.12);
}

/* Loading animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .cart-header,
  .cart-summary {
    border-left-width: 2px;
  }

  .cart-group {
    border-left-width: 2px;
  }
}

/* Smooth transitions */
.cart-group,
.cart-header,
.cart-summary {
  transition: all 0.3s ease;
}

/* Text selection prevention for better UX */
.cart-header,
.cart-summary {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Focus states for accessibility */
button:focus {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

select:focus,
input:focus {
  outline: none;
  ring: 2px;
  ring-color: var(--color-primary);
}

/* Improved spacing and typography */
.space-y-1 > * + * {
  margin-top: 0.25rem;
}

.space-y-2 > * + * {
  margin-top: 0.5rem;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

.space-x-2 > * + * {
  margin-left: 0.5rem;
}

.space-x-3 > * + * {
  margin-left: 0.75rem;
}

.space-x-6 > * + * {
  margin-left: 1.5rem;
}
</style>
